import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import FeeStructureForm from "./FeeStructureForm";
import ArchiveEntityDialog from "../common/ArchiveEntityDialog";
import {
  useArchiveFeeStructure,
  useCreateFeeStructure,
  useFeeStructures
} from "../../../hooks/api/education/useFeeStructure";
import type { FeeStructure } from "./types/feeStructure";

const isArchived = (status?: string | null) => status === "ARCHIVED";

const FeeStructureList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const [openForm, setOpenForm] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<FeeStructure | null>(null);
  const {
    data: feeStructures = [],
    isPending,
    refetch
  } = useFeeStructures({ size: 50 });
  const createMutation = useCreateFeeStructure();
  const archiveMutation = useArchiveFeeStructure();

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "loading-fee-structures",
          "Loading fee structures..."
        )}
      />
    );
  }

  return (
    <Box data-testid="fee-structure-list-page" sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" component="h1">
          {t("fee-structures-title", "Fee structures")}
        </Typography>
        <AppButton onClick={() => setOpenForm(true)}>
          {t("add-fee-structure", "Add fee structure")}
        </AppButton>
      </Box>

      <FeeStructureForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        loading={createMutation.isPending}
        onSubmit={async (input) => {
          await createMutation.mutateAsync(input);
          setOpenForm(false);
          refetch();
        }}
      />

      <ArchiveEntityDialog
        open={archiveTarget != null}
        entityLabel={archiveTarget?.name ?? ""}
        loading={archiveMutation.isPending}
        onClose={() => setArchiveTarget(null)}
        onConfirm={async (reason) => {
          if (!archiveTarget?.id) {
            return;
          }
          await archiveMutation.mutateAsync({
            feeStructureId: archiveTarget.id,
            expectedVersion: archiveTarget.version,
            reason: reason || undefined
          });
          setArchiveTarget(null);
          refetch();
        }}
      />

      {feeStructures.length === 0 ? (
        <Typography color="text.secondary">
          {t(
            "fee-structures-empty",
            "No fee structures yet. Add fees for a billing period."
          )}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {feeStructures.map((fee) => (
            <Box
              component="li"
              key={fee.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                opacity: isArchived(fee.status) ? 0.65 : 1
              }}
            >
              <Box>
                <Typography fontWeight={600}>{fee.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("fee-amount-label", "Amount")}: {fee.amount} ·{" "}
                  {t("billing-period-id", "Period")}: {fee.billingPeriodId}
                  {fee.status ? ` · ${fee.status}` : ""}
                </Typography>
              </Box>
              {!isArchived(fee.status) && fee.id ? (
                <AppButton variant="text" onClick={() => setArchiveTarget(fee)}>
                  {t("archive", "Archive")}
                </AppButton>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FeeStructureList;
